import * as Types from '@seeqdev/api/graphql/types';

export type GqlGetWorkbookWithApprovalFlowQueryVariables = Types.Exact<{
  workbookId: Types.Scalars['ID']['input'];
  worksheetId: Types.Scalars['ID']['input'];
  workstepId: Types.InputMaybe<Types.Scalars['ID']['input']>;
  skipWorksheets: Types.Scalars['Boolean']['input'];
}>;


export type GqlGetWorkbookWithApprovalFlowQuery = { __typename?: 'Query', workbook: { __typename?: 'WorkbookGraphQL', id: string, name: string, description: string | undefined, type: string, createdAt: string, updatedAt: string, isArchived: boolean, parentFolderId: string | undefined, isLocked: boolean, lockedMetadata: string | undefined, workbookState: string | undefined, evidenceTableDefinitionId: string | undefined, serverErrors: Array<{ __typename?: 'ServerError', field: string, message: string, path: Array<string> }>, effectivePermissions: { __typename?: 'PermissionsGraphQL', read: boolean, write: boolean, manage: boolean }, owner: { __typename?: 'IdentityPreviewGraphQL', id: string | undefined, name: string | undefined, email: string | undefined, username: string | undefined, type: string | undefined } | undefined, ancestors: Array<{ __typename?: 'ItemPreviewGraphQL', id: string | undefined, name: string | undefined, type: string | undefined, translationKey: string | undefined }>, worksheets?: Array<{ __typename?: 'WorksheetGraphQL', id: string, name: string, createdAt: string, updatedAt: string, isArchived: boolean, isDashboard: boolean, duplicationErrorDocument: string | undefined, boardId: string | undefined, report: { __typename?: 'ItemPreviewGraphQL', id: string | undefined, name: string | undefined, type: string | undefined } | undefined, currentWorkstep: { __typename?: 'WorkstepGraphQL', id: string } | undefined }>, selectedWorksheet: { __typename?: 'WorksheetGraphQL', id: string, name: string, isDashboard: boolean, boardId: string | undefined, isArchived: boolean, createdAt: string, updatedAt: string, duplicationErrorDocument: string | undefined, serverErrors: Array<{ __typename?: 'ServerError', field: string, message: string, path: Array<string> }>, report: { __typename?: 'ItemPreviewGraphQL', id: string | undefined, name: string | undefined, type: string | undefined } | undefined, currentWorkstep: { __typename?: 'WorkstepGraphQL', id: string, name: string, type: string, createdAt: string, data: string, previous: string | undefined, next: string | undefined, last: string | undefined } | undefined, requestedWorkstep: { __typename?: 'WorkstepGraphQL', id: string, name: string, type: string, createdAt: string, data: string, previous: string | undefined, next: string | undefined, last: string | undefined } | undefined } | undefined, attachments: Array<{ __typename?: 'ApprovalFlowAttachmentGraphQL', id: string, itemId: string, approvalFlowId: string, assignedBy: string, createdAt: string, archived: boolean }>, approvalFlows: Array<{ __typename?: 'ApprovalFlowGraphQL', id: string, name: string, archived: boolean, createdAt: string, updatedAt: string | undefined, steps: Array<{ __typename?: 'ApprovalFlowStepGraphQL', id: string, approvalFlowId: string, name: string, lockRequired: boolean, position: number, archived: boolean, status: Types.GqlApprovalFlowStepStatus | undefined, createdAt: string, updatedAt: string | undefined, approval: { __typename?: 'ApprovalFlowAssignmentGraphQL', approvedAt: string, approver: { __typename?: 'IdentityPreviewGraphQL', name: string | undefined, email: string | undefined } } | undefined, permissions: { __typename?: 'PermissionsGraphQL', read: boolean, write: boolean, manage: boolean } }> }>, approvalFlowAssignments: Array<{ __typename?: 'ApprovalFlowAssignmentGraphQL', id: string, approvalFlowId: string, stepId: string, itemId: string, approvedBy: string, approvedAt: string }> } };

/* Appended by scripts/generate-gql-apis.ts: hooks dispatch through sqWorkbookGqlApi so tests can
   intercept them with jest.spyOn(sqWorkbookGqlApi, 'getWorkbookWithApprovalFlow'). */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAxiosInstance } from '../../../src/reactQuery/ApiProvider.context';
import { sqWorkbookGqlApi } from './sqWorkbookGqlApi';

export const GetWorkbookWithApprovalFlowDocument = `query GetWorkbookWithApprovalFlow($workbookId: ID!, $worksheetId: ID!, $workstepId: ID, $skipWorksheets: Boolean!) {
  workbook(id: $workbookId) {
    ...WorkbookCore
    ...ApprovalFlowSection
  }
}

fragment ApprovalFlowSection on WorkbookGraphQL {
  attachments {
    id
    itemId
    approvalFlowId
    assignedBy
    createdAt
    archived
  }
  approvalFlows {
    id
    name
    archived
    createdAt
    updatedAt
    steps {
      id
      approvalFlowId
      name
      lockRequired
      position
      archived
      status
      approval {
        approvedAt
        approver {
          name
          email
        }
      }
      createdAt
      updatedAt
      permissions {
        read
        write
        manage
      }
    }
  }
  approvalFlowAssignments {
    id
    approvalFlowId
    stepId
    itemId
    approvedBy
    approvedAt
  }
}

fragment WorkbookCore on WorkbookGraphQL {
  serverErrors {
    field
    message
    path
  }
  id
  name
  description
  type
  createdAt
  updatedAt
  isArchived
  effectivePermissions {
    read
    write
    manage
  }
  owner {
    id
    name
    email
    username
    type
  }
  parentFolderId
  ancestors {
    id
    name
    type
    translationKey
  }
  isLocked
  lockedMetadata
  workbookState
  evidenceTableDefinitionId
  worksheets @skip(if: $skipWorksheets) {
    id
    name
    createdAt
    updatedAt
    isArchived
    isDashboard
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    boardId
    currentWorkstep {
      id
    }
  }
  selectedWorksheet: worksheet(id: $worksheetId) {
    id
    name
    serverErrors {
      field
      message
      path
    }
    isDashboard
    boardId
    isArchived
    createdAt
    updatedAt
    duplicationErrorDocument
    report {
      id
      name
      type
    }
    currentWorkstep {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
    requestedWorkstep: workstep(id: $workstepId) {
      id
      name
      type
      createdAt
      data
      previous
      next
      last
    }
  }
}`;

export const useGetWorkbookWithApprovalFlowQuery = <TData = GqlGetWorkbookWithApprovalFlowQuery, TError = unknown>(
  variables: GqlGetWorkbookWithApprovalFlowQueryVariables,
  options?: Omit<UseQueryOptions<GqlGetWorkbookWithApprovalFlowQuery, TError, TData>, 'queryKey'> & {
    queryKey?: UseQueryOptions<GqlGetWorkbookWithApprovalFlowQuery, TError, TData>['queryKey'];
  },
) => {
  const axiosInstance = useAxiosInstance();
  return useQuery<GqlGetWorkbookWithApprovalFlowQuery, TError, TData>({
    queryKey: ['GetWorkbookWithApprovalFlow', variables],
    queryFn: () => sqWorkbookGqlApi.getWorkbookWithApprovalFlow(variables, { axiosInstance }),
    ...options,
  });
};

useGetWorkbookWithApprovalFlowQuery.getKey = (variables: GqlGetWorkbookWithApprovalFlowQueryVariables) => ['GetWorkbookWithApprovalFlow', variables];
